import {ArrowRightIcon, MinusIcon, PlusIcon} from "@heroicons/react/20/solid";
import {CreditCalculatorState, Ride} from "pages/Front/Booking/Credits/CreditCalculator/types";
import {CREDITS_CLASSES, RIDES} from "pages/Front/Booking/Credits/CreditCalculator/constants";
import {formatChf} from "pages/Front/Booking/Credits/CreditCalculator/utils";
import React from "react";

interface StepperRowProps {
    ride: Ride;
    count: number;
    onIncrement: () => void;
    onDecrement: () => void;
}

function StepperRow({ride, count, onIncrement, onDecrement}: StepperRowProps) {
    const stepperButtonClasses = `flex h-8 w-8 items-center justify-center rounded-md border border-line bg-white text-gray-700 transition hover:bg-gray-50 focus-visible:outline-none focus-visible:ring-2 ${CREDITS_CLASSES.ring} active:bg-panel disabled:cursor-not-allowed disabled:opacity-40`;

    return (
        <div className="StepperRow flex items-center gap-2.5 py-1.5">
            <p className="flex-1 text-sm font-medium text-ink">{ride.name}</p>
            <p className="w-16 text-xs text-ink-muted">{ride.credits} Credits</p>
            <button type="button" onClick={onDecrement} disabled={count === 0} aria-label={`Weniger ${ride.name}`} className={stepperButtonClasses}>
                <MinusIcon className="h-4 w-4" aria-hidden="true" />
            </button>
            <p className="w-6 text-center text-sm font-medium text-ink" aria-live="polite">
                {count}
            </p>
            <button type="button" onClick={onIncrement} aria-label={`Mehr ${ride.name}`} className={stepperButtonClasses}>
                <PlusIcon className="h-4 w-4" aria-hidden="true" />
            </button>
        </div>
    );
}

interface CreditCalculatorProps {
    calculator: CreditCalculatorState;
}

export function CreditCalculator({calculator}: CreditCalculatorProps) {
    const {selection, creditsNeeded, recommendation, needsCombination, increment, decrement} = calculator;

    return (
        <section aria-labelledby="credit-calculator-heading" className="rounded-xl bg-panel px-4 py-3.5">
            <h3 id="credit-calculator-heading" className="text-sm font-semibold text-ink">
                Rechne es aus
            </h3>
            <p className="mb-2.5 text-xs text-ink-muted">Was möchtest du fahren? Wir zeigen dir das passende Paket.</p>

            <div role="group" aria-label="Fahrten auswählen">
                {RIDES.map(ride => (
                    <StepperRow
                        key={ride.id}
                        ride={ride}
                        count={selection[ride.id]}
                        onIncrement={() => increment(ride.id)}
                        onDecrement={() => decrement(ride.id)}
                    />
                ))}
            </div>

            <div className="flex flex-wrap items-baseline gap-2 border-t border-line pt-2.5" aria-live="polite">
                <span className="text-xs text-ink-muted">Benötigt:</span>
                <span className={`text-lg font-bold ${CREDITS_CLASSES.text}`}>{creditsNeeded}</span>
                <span className="text-xs text-ink-muted">Credits</span>
                <span className="flex-1" />
                {creditsNeeded === 0 && <span className="text-sm text-ink-muted">Wähle Fahrten aus</span>}
                {creditsNeeded > 0 && recommendation && (
                    <span className={`inline-flex items-center gap-1 text-sm font-bold ${CREDITS_CLASSES.text}`}>
                        <ArrowRightIcon className="h-3.5 w-3.5" aria-hidden="true" />
                        {recommendation.pack.credits} Credits · {formatChf(recommendation.pack.priceChf)}
                        {recommendation.surplus > 0 && ` · ${recommendation.surplus} übrig`}
                    </span>
                )}
                {needsCombination && <span className="text-sm text-ink-secondary">Mehrere Pakete kombinieren</span>}
            </div>
        </section>
    );
}
